if (!customElements.get('google-reviews-carousel')) {
  customElements.define(
    'google-reviews-carousel',
    class GoogleReviewsCarousel extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.google-reviews__viewport');
        this.track = this.querySelector('.google-reviews__track');
        this.toggleButton = this.querySelector('.google-reviews__toggle');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        // Phones step one centered review at a time; wider screens scroll continuously
        this.stepMedia = window.matchMedia('(max-width: 749px)');
        this.expandedCard = null;
        this.lastWidth = 0;
        if (!this.track || this.track.children.length === 0) return;

        this.toggleButton.addEventListener('click', () => this.setPaused(!this.classList.contains('is-paused')));
        // Delegated so the looping copies' buttons work too
        this.track.addEventListener('click', (event) => {
          const button = event.target.closest('.google-review__more');
          if (!button) return;
          const card = button.closest('.google-review');
          if (card === this.expandedCard) {
            this.collapse();
          } else {
            this.expand(card);
          }
        });
        this.onDocumentClick = (event) => {
          if (this.expandedCard && !this.expandedCard.contains(event.target)) this.collapse();
        };
        this.onKeydown = (event) => {
          if (event.key === 'Escape' && this.expandedCard) this.collapse({ focus: true });
        };
        // Hold still while a review is selected in the theme editor
        this.addEventListener('shopify:block:select', () => this.classList.add('is-editor-paused'));
        this.addEventListener('shopify:block:deselect', () => this.classList.remove('is-editor-paused'));
        this.reducedMotion.addEventListener('change', () => this.build());
        // Line breaks shift once the web font arrives
        document.fonts?.ready.then(() => this.measure());

        this.track.addEventListener('transitionend', (event) => {
          if (event.target === this.track) this.normalizeStep();
        });
        this.viewport.addEventListener('touchstart', (event) => (this.touchX = event.touches[0].clientX), {
          passive: true,
        });
        this.viewport.addEventListener(
          'touchend',
          (event) => {
            if (!this.classList.contains('is-stepped')) return;
            const distance = event.changedTouches[0].clientX - this.touchX;
            if (Math.abs(distance) < 40) return;
            // Swiping away from an expanded review closes it on the way
            this.collapse();
            this.step(distance < 0 ? 1 : -1);
            this.startTimer();
          },
          { passive: true }
        );

        this.resizeObserver = new ResizeObserver(() => {
          if (this.viewport.clientWidth === this.lastWidth) return;
          this.lastWidth = this.viewport.clientWidth;
          this.build();
        });
        this.resizeObserver.observe(this.viewport);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        clearInterval(this.timer);
        this.collapse();
      }

      build() {
        this.collapse();
        this.loop();
        this.measure();
      }

      // Appends copies of the reviews so the track can loop seamlessly by translating exactly one set's width.
      // Unlike the about-carousel's copies they stay clickable (for "See more"), but are hidden from assistive
      // tech and the tab order, which reach the originals instead.
      loop() {
        this.track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
        this.classList.remove('is-animated', 'is-stepped');
        this.track.style.removeProperty('transform');
        clearInterval(this.timer);
        this.toggleButton.hidden = true;
        if (this.reducedMotion.matches) return;

        // Measure in the animated layout, which drops the static scroll padding
        this.classList.add('is-animated');
        const originals = Array.from(this.track.children);
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const distance = this.track.scrollWidth + gap;
        const speed = parseFloat(this.dataset.speed) || 30;
        if (distance <= gap) {
          this.classList.remove('is-animated');
          return;
        }

        if (this.stepMedia.matches) {
          // Three sets in a row: the step index stays in the middle one, so there's always a review on both sides
          this.appendCopies(originals, 2);
          this.classList.add('is-stepped');
          this.stepCount = originals.length;
          this.stepDelay = (parseFloat(this.dataset.stepSeconds) || 6) * 1000;
          this.goTo(this.stepCount, false);
          this.startTimer();
        } else {
          this.appendCopies(originals, Math.max(1, Math.ceil(this.viewport.clientWidth / distance)));
          this.style.setProperty('--gr-distance', `${distance}px`);
          this.style.setProperty('--gr-duration', `${distance / speed}s`);
        }
        this.toggleButton.hidden = false;
      }

      appendCopies(originals, copies) {
        for (let i = 0; i < copies; i++) {
          originals.forEach((card) => {
            const clone = card.cloneNode(true);
            clone.setAttribute('data-clone', '');
            clone.setAttribute('aria-hidden', 'true');
            clone.removeAttribute('data-shopify-editor-block');
            clone.querySelectorAll('button').forEach((button) => button.setAttribute('tabindex', '-1'));
            this.track.appendChild(clone);
          });
        }
      }

      startTimer() {
        clearInterval(this.timer);
        this.timer = setInterval(() => {
          if (!this.isHeld()) this.step(1);
        }, this.stepDelay);
      }

      // The same holds that pause the desktop scroll (except hover, which phones don't have)
      isHeld() {
        return (
          document.hidden ||
          this.expandedCard !== null ||
          this.classList.contains('is-paused') ||
          this.classList.contains('is-editor-paused') ||
          this.hasKeyboardFocus()
        );
      }

      hasKeyboardFocus() {
        try {
          return this.viewport.matches(':has(:focus-visible)');
        } catch {
          return false; // No :has() support
        }
      }

      step(direction) {
        this.normalizeStep();
        this.goTo(this.stepIndex + direction);
      }

      // Centers the review at `index` in the viewport
      goTo(index, animate = true) {
        const card = this.track.children[index];
        if (!card) return;
        this.stepIndex = index;
        const offset = (this.viewport.clientWidth - card.offsetWidth) / 2 - card.offsetLeft;
        if (!animate) this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${offset}px)`;
        if (!animate) {
          // Apply the jump before transitions come back on
          this.track.getBoundingClientRect();
          this.track.style.removeProperty('transition');
        }
      }

      // After moving into the first or last set, jumps unseen to the same review in the middle set
      normalizeStep() {
        if (!this.classList.contains('is-stepped') || this.expandedCard) return;
        if (this.stepIndex >= this.stepCount * 2) {
          this.goTo(this.stepIndex - this.stepCount, false);
        } else if (this.stepIndex < this.stepCount) {
          this.goTo(this.stepIndex + this.stepCount, false);
        }
      }

      // Offers "See more" only on reviews that are actually cut off at the line limit
      measure() {
        this.track.querySelectorAll('.google-review').forEach((card) => {
          if (card === this.expandedCard) return;
          const text = card.querySelector('.google-review__text p');
          card.classList.toggle('has-more', Boolean(text) && text.scrollHeight > text.clientHeight + 1);
        });
      }

      expand(card) {
        this.collapse();
        this.expandedCard = card;
        card.classList.add('is-expanded');
        this.classList.add('has-expanded');
        // On phones, bring a half-visible neighbor to the center to read it
        if (this.classList.contains('is-stepped')) {
          this.goTo(Array.prototype.indexOf.call(this.track.children, card));
        }
        const button = card.querySelector('.google-review__more');
        button.setAttribute('aria-expanded', 'true');
        button.textContent = 'See less';
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
      }

      collapse({ focus = false } = {}) {
        const card = this.expandedCard;
        if (!card) return;
        this.expandedCard = null;
        card.classList.remove('is-expanded');
        this.classList.remove('has-expanded');
        const button = card.querySelector('.google-review__more');
        button.setAttribute('aria-expanded', 'false');
        button.textContent = 'See more';
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        if (focus && !card.hasAttribute('data-clone')) button.focus();
        if (this.classList.contains('is-stepped')) {
          this.normalizeStep();
          // A full turn for the review just read before moving on
          this.startTimer();
        }
      }

      setPaused(paused) {
        this.classList.toggle('is-paused', paused);
        this.toggleButton.setAttribute(
          'aria-label',
          paused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause
        );
      }
    }
  );
}
